import type { Model } from './model';
import { Status } from './status';

/** Collection intent is independent of status and whether a payment method is present. */
export enum CollectionMode {
  GATEWAY = 'GATEWAY',
  OUT_OF_BAND = 'OUT_OF_BAND',
  /**
   * A checkout balance the buyer chose to pay later. Outstanding until staff collect it through the
   * assigned gateway or record an external receipt; never charged by scheduled processing.
   */
  PAY_LATER = 'PAY_LATER',
}

export enum ExternalPaymentChannel {
  CHECK = 'CHECK',
  WIRE = 'WIRE',
  ACH = 'ACH',
  CASH = 'CASH',
  OTHER = 'OTHER',
}

export interface ExternalSettlement {
  /** Full installment amount, in cents. */
  amount: number;
  currency: string;
  receivedDate: string;
  channel: ExternalPaymentChannel;
  actorUserId: string;
  actorStaffId: string;
  actorName: string;
  recordedAt: string;
  reference: string | null;
  note: string | null;
  attachment?: { storageItemId: string; filename: string; mimeType: string } | null;
}

export interface SettleExternalPaymentRequest {
  /** Full installment amount, in cents. */
  amount: number;
  currency: string;
  receivedDate: string;
  channel: ExternalPaymentChannel;
  reference?: string;
  note?: string;
  attachmentStorageItemId?: string;
}

/**
 * Whether staff may record an external receipt for this payment: a pending seller Bill To
 * installment, or a Pay Later balance still outstanding — including after a declined gateway
 * attempt. A settled or gateway-collected payment never qualifies.
 */
export function canSettleExternally(
  payment: Pick<Model, 'collectionMode' | 'status' | 'externalSettlement' | 'paymentDate'>
): boolean {
  if (payment.externalSettlement || payment.paymentDate) return false;
  if (payment.collectionMode === CollectionMode.OUT_OF_BAND) return payment.status === Status.PENDING;
  if (payment.collectionMode === CollectionMode.PAY_LATER) {
    return payment.status === Status.PENDING || payment.status === Status.FAILED;
  }
  return false;
}

/** Display label for a payment's collection state, or null to fall back to its raw status. */
export function collectionStatusLabel(
  payment: Pick<Model, 'collectionMode' | 'status' | 'externalSettlement'>
): string | null {
  if (payment.collectionMode === CollectionMode.OUT_OF_BAND && payment.status === Status.PENDING) {
    return 'Awaiting external settlement';
  }
  if (payment.collectionMode === CollectionMode.PAY_LATER) {
    if (payment.status === Status.PENDING) return 'Pay Later outstanding';
    if (payment.status === Status.FAILED) return 'Pay Later collection failed';
  }
  if (payment.externalSettlement && payment.status === Status.PAID) return 'Externally settled';
  return null;
}
